/**
 * ForeignKeyPopovers — Designer-side popover wrappers for the add + drop FK
 * forms. Sqlite renders disabled triggers with the recreate-path tooltip; the
 * form bodies are never mounted on sqlite. Mirrors `PrimaryKeyPopovers` from
 * Slice B1.
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice B2.
 */
import { useState } from 'react';
import { Link2, Plus, Trash2 } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../../../components/ui/popover';
import {
  getAddForeignKeyUnsupportedTooltip,
  isAddForeignKeySupported,
} from '../../edit/edits/addForeignKey';
import {
  getDropForeignKeyUnsupportedTooltip,
  isDropForeignKeySupported,
} from '../../edit/edits/dropForeignKey';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { DisabledAffordance } from './DisabledAffordance';
import { ForeignKeyAddFormBody } from './ForeignKeyAddFormBody';
import { ForeignKeyDropFormBody } from './ForeignKeyDropFormBody';

export interface ForeignKeyAddPopoverProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId?: string;
  readonly onCommitted?: () => void;
}

export function ForeignKeyAddPopover(props: ForeignKeyAddPopoverProps) {
  const { editContext, testId = 'foreign-key-add-popover', onCommitted } = props;
  const engine = editContext.service.engineId;
  const [open, setOpen] = useState(false);

  if (!isAddForeignKeySupported(engine)) {
    return (
      <DisabledAffordance
        testId={`${testId}-trigger`}
        tooltip={getAddForeignKeyUnsupportedTooltip(engine)}
        variant="ghost"
      >
        <Plus className="h-3.5 w-3.5" />
        Foreign key
      </DisabledAffordance>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          data-testid={`${testId}-trigger`}
          aria-label="Add foreign key"
        >
          <Link2 className="h-3.5 w-3.5" />
          Foreign key
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        collisionPadding={16}
        className="max-h-[min(480px,var(--radix-popover-content-available-height))] w-80 overflow-y-auto"
        data-testid={testId}
      >
        {open ? (
          <ForeignKeyAddFormBody
            editContext={editContext}
            testId={testId}
            onCommitted={() => {
              setOpen(false);
              onCommitted?.();
            }}
            onCancel={() => setOpen(false)}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

export interface ForeignKeyDropPopoverProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId?: string;
  readonly onCommitted?: () => void;
}

export function ForeignKeyDropPopover(props: ForeignKeyDropPopoverProps) {
  const { editContext, testId = 'foreign-key-drop-popover', onCommitted } = props;
  const engine = editContext.service.engineId;
  const [open, setOpen] = useState(false);

  if (!isDropForeignKeySupported(engine)) {
    return (
      <DisabledAffordance
        testId={`${testId}-trigger`}
        tooltip={getDropForeignKeyUnsupportedTooltip(engine)}
        variant="ghost"
      >
        <Trash2 className="h-3.5 w-3.5" />
        Drop foreign key
      </DisabledAffordance>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          data-testid={`${testId}-trigger`}
          aria-label="Drop foreign key"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Drop foreign key
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        collisionPadding={16}
        className="w-80"
        data-testid={testId}
      >
        {open ? (
          <ForeignKeyDropFormBody
            editContext={editContext}
            testId={testId}
            onCommitted={() => {
              setOpen(false);
              onCommitted?.();
            }}
            onCancel={() => setOpen(false)}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
